<script setup lang="ts">
  import pluralize from 'pluralize';

  import type { KnowledgeCitation } from '@stuut/api/models';
  import { HoverCard, HoverCardContent, HoverCardTrigger } from '@ui/components/hover-card';

  import { getArticleRouteLocation } from '@/utils/playbookDomains';

  interface Props {
    citations: KnowledgeCitation[];
    label?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    label: 'Citation',
  });
</script>

<template>
  <HoverCard v-if="props.citations?.length" :open-delay="0">
    <HoverCardTrigger as-child>
      <span class="flex items-center gap-x-1.5 text-xs text-muted-foreground text-nowrap">
        <i class="fas fa-quote-left" />
        <span
          class="underline decoration-dashed decoration-muted-foreground decoration-1 underline-offset-4 cursor-pointer hover:decoration-solid"
          >{{ pluralize(props.label, props.citations.length, true) }}</span
        >
      </span>
    </HoverCardTrigger>

    <HoverCardContent class="flex flex-col gap-y-4 w-120 max-h-80 overflow-y-auto">
      <strong>Citations:</strong>

      <div class="flex flex-col gap-y-4">
        <div
          v-for="citation in props.citations"
          :key="citation.id"
          class="flex flex-col gap-y-6 gap-x-2.5 p-4 bg-muted rounded"
        >
          <div class="flex items-start justify-between">
            <div class="flex flex-col gap-y-2 text-xs">
              <span class="font-semibold">{{ citation.article_title }}</span>
              <span class="line-clamp-4 text-muted-foreground pl-2.5 border-l-4 border-border">{{
                citation.excerpt
              }}</span>
            </div>

            <RouterLink
              class="text-link text-2xs text-nowrap"
              :to="getArticleRouteLocation(citation.article_collection, citation.article_id) ?? {}"
              target="_blank"
              rel="noopener noreferrer"
              >View Article
            </RouterLink>
          </div>

          <div class="flex flex-col gap-y-1 text-2xs">
            <span class="line-clamp-2 text-muted-foreground space-x-1">
              <i class="far fa-robot" />
              <strong>Agent Reasoning:</strong>
            </span>
            <em>{{ citation.agent_reasoning }}</em>
          </div>
        </div>
      </div>
    </HoverCardContent>
  </HoverCard>
</template>
